import { useState } from "react";
import { AlertTriangle, AlertCircle, Info, CheckCircle2, TrendingDown, TrendingUp, Minus, Sparkles, Loader2 } from "lucide-react";
import { apiFetch, API } from "@/utils/api";

const SEVERITY_CONFIG = {
  critical: {
    icon: AlertTriangle,
    bg: "bg-red-50",
    border: "border-red-200",
    iconColor: "text-red-600",
    badge: "bg-red-100 text-red-700",
    label: "Crítico",
  },
  important: {
    icon: AlertCircle,
    bg: "bg-amber-50",
    border: "border-amber-200",
    iconColor: "text-amber-600",
    badge: "bg-amber-100 text-amber-700",
    label: "Importante",
  },
  informative: {
    icon: Info,
    bg: "bg-blue-50",
    border: "border-blue-200",
    iconColor: "text-blue-600",
    badge: "bg-blue-100 text-blue-700",
    label: "Informativo",
  },
};

const RANGE_LABELS = { short: "Corto", medium: "Medio", long: "Largo" };

function DirectionIcon({ direction }) {
  if (direction === "declining") return <TrendingDown className="w-3 h-3 text-red-500" />;
  if (direction === "rising") return <TrendingUp className="w-3 h-3 text-emerald-500" />;
  return <Minus className="w-3 h-3 text-slate-400" />;
}

function RangeBadges({ ranges }) {
  if (!ranges || Object.keys(ranges).length === 0) return null;
  return (
    <div className="flex gap-1.5 mt-2">
      {Object.entries(ranges).map(([range, data]) => {
        if (!data || !data.detected) return null;
        return (
          <span
            key={range}
            className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600"
          >
            <DirectionIcon direction={data.direction} />
            {RANGE_LABELS[range] || range}
          </span>
        );
      })}
    </div>
  );
}

function ExplainBlock({ tensionId }) {
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const fetchExplanation = async () => {
    if (explanation) {
      setVisible(!visible);
      return;
    }
    setLoading(true);
    setVisible(true);
    try {
      const res = await apiFetch(`${API}/ai/explain/${tensionId}`);
      if (res.ok) {
        const data = await res.json();
        setExplanation(data.explanation);
      } else {
        setExplanation("No se pudo generar la explicación.");
      }
    } catch (e) {
      setExplanation("Error de conexión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2">
      <button
        onClick={fetchExplanation}
        className="inline-flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium"
      >
        <Sparkles className="w-3 h-3" />
        {visible ? "Ocultar" : "¿Por qué?"}
      </button>
      {visible && (
        <div className="mt-2 p-2.5 rounded bg-white/60 border border-violet-100">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-violet-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              Analizando...
            </div>
          ) : (
            <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{explanation}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function TensionCards({ tensions }) {
  if (!tensions || tensions.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        <div>
          <p className="text-sm font-medium text-emerald-900">Sin tensiones detectadas</p>
          <p className="text-xs text-emerald-700">Todos los indicadores financieros están dentro de rangos normales</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {tensions.map((tension) => {
        const config = SEVERITY_CONFIG[tension.severity] || SEVERITY_CONFIG.informative;
        const Icon = config.icon;
        return (
          <div
            key={tension.tension_id}
            className={`${config.bg} border ${config.border} rounded-lg p-4`}
          >
            <div className="flex items-start gap-3">
              <Icon className={`w-5 h-5 ${config.iconColor} mt-0.5 shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-slate-900">{tension.title}</h4>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${config.badge}`}>
                    {config.label}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mt-1">{tension.detail}</p>
                <RangeBadges ranges={tension.ranges} />
                <ExplainBlock tensionId={tension.tension_id} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
